import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { translate, TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { BehaviorSubject, map, switchMap } from 'rxjs';

import { makePTableCols } from '../../../../common/p-table/make-p-table-cols';
import { IvrService } from '../../../../services/call/ivr/ivr.service';
import { DataTableModule } from '../../../generic/data-table/data-table.module';
import { createIVREditorRow } from '../helpers/create-ivr-editor-row/create-ivr-editor-row';
import { IVREditorRow } from '../helpers/create-ivr-editor-row/interfaces';
import { IVROptionTableEditorComponent } from '../ivr-option-table-editor/ivr-option-table-editor.component';

@UntilDestroy()
@Component({
  selector: 'app-ivr-table-editor',
  standalone: true,
  templateUrl: './ivr-table-editor.component.html',
  styleUrls: ['./ivr-table-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonModule,
    CommonModule,
    ToastModule,
    DataTableModule,
    DynamicDialogModule,
    MatIconModule,
    TranslocoModule,
  ],
  providers: [MessageService, DialogService],
})
export class IVRTableEditorComponent {
  //////////////////////////////////////////////////////////////////////////////////////////
  // Services
  //////////////////////////////////////////////////////////////////////////////////////////

  private readonly dialogService = inject(DialogService);

  private readonly ivrService = inject(IvrService);

  //////////////////////////////////////////////////////////////////////////////////////////
  // Data
  //////////////////////////////////////////////////////////////////////////////////////////

  public readonly cols = makePTableCols({
    columns: ['displayName', 'description', 'optionsCount', 'edit'],
    translationScope: 'ivr-table-editor',
  });

  private readonly refresh$ = new BehaviorSubject<void>(null);

  public readonly tableRows$ = this.refresh$.pipe(
    switchMap(() => this.ivrService.getDocsArray()),
    map((ivrs) => ivrs.map((ivr) => createIVREditorRow(ivr))),
  );

  //////////////////////////////////////////////////////////////////////////////////////////
  // Edit IVR
  //////////////////////////////////////////////////////////////////////////////////////////

  public openEditDialog(rowData: IVREditorRow) {
    const dialog = this.dialogService.open(IVROptionTableEditorComponent, {
      header: translate('ivr-table-editor.editDialogHeader', { name: rowData.displayName }),
      width: 'clamp(32rem, 90%, 80rem)',
      height: '90vh',
      closeOnEscape: true,
      dismissableMask: true,
      baseZIndex: 900,
      autoZIndex: false,
      data: {
        ivrId: rowData.id,
      },
    });

    dialog.onClose.pipe(untilDestroyed(this)).subscribe(() => {
      this.refresh$.next();
    });
  }
}
